import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, MessageCircle, Pencil, ShieldCheck, Smartphone } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneField } from '@/components/booking/phone-field';
import { splitPhone, countryByIso } from '@/lib/countries';

const CHANNEL = {
  whatsapp: { icon: MessageCircle, word: 'WhatsApp' },
  sms: { icon: Smartphone, word: 'SMS' },
};

// The number, readable. E.164 is what we send and the worst thing to read back
// to someone checking their own number for a typo - +919160436269 is a wall of
// digits, +91 9160436269 is a phone number.
function pretty(e164) {
  const p = splitPhone(String(e164 || ''), null);
  const c = countryByIso(p.iso);
  return c ? `+${c.dial} ${p.national}` : String(e164 || '');
}

/**
 * Verification at the point of commitment, not at the point of typing.
 *
 * The phone field is on step 1 and the submit is on step 3. Prompting under the
 * field meant someone could answer every qualifying question, reach "Book my
 * call", and only then be told to go back two steps for something they had
 * walked past - a dead end at the exact moment they had decided to buy.
 *
 * So the gate lives here. It shows the number BEFORE anything is sent, because
 * the people this rescues are the ones who mistyped: they need to see the
 * number they are about to be texted while they can still fix it, and fixing it
 * happens right here rather than by navigating back.
 *
 * Friction at the end is also cheaper friction. By this point the visitor has
 * invested three steps; asking at step 1 taxes everyone, including the many who
 * would never have finished anyway.
 */
export function VerifyGate({ v, phone, onPhoneChange, busy }) {
  const [code, setCode] = useState('');
  const [editing, setEditing] = useState(false);
  const box = useRef(null);

  useEffect(() => { if (v.status === 'sent' && box.current) box.current.focus(); }, [v.status]);
  // Digits typed against an abandoned challenge are not an answer to the next
  // one - leaving them would auto-submit a stale code the moment six survive.
  useEffect(() => { if (v.status === 'idle') setCode(''); }, [v.status]);

  // Six digits IS the answer - checking it is not a second decision.
  useEffect(() => {
    if (code.length === 6 && v.status === 'sent') v.verify(code);
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nothing to show before a number exists - "We'll send a code to" followed
  // by empty space reads as a bug. The button still says "Verify &" so the
  // extra step is never a surprise, and validation asks for the number first.
  const hasPhone = String(phone || '').replace(/\D/g, '').length >= 6;
  if (!v.active || (!hasPhone && !editing)) return null;

  if (v.verified) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">
        <CheckCircle2 className="size-4 shrink-0" />
        <span className="tabular-nums">{pretty(phone)}</span> verified
      </div>
    );
  }

  const Icon = (CHANNEL[v.channel] || CHANNEL.sms).icon;
  const other = v.channels.filter((c) => c !== v.channel);

  return (
    <div className="space-y-2.5 rounded-md border border-border bg-muted/30 p-3">
      {editing ? (
        <div className="space-y-2">
          <p className="text-[13px] font-medium">Which number should we send the code to?</p>
          <PhoneField
            id="verify-phone"
            value={phone}
            iso={splitPhone(phone, null).iso}
            onChange={onPhoneChange}
            autoComplete="tel"
          />
          <Button type="button" size="sm" variant="secondary" onClick={() => setEditing(false)}>Use this number</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-[13px]">
            <ShieldCheck className="size-4 shrink-0 text-muted-foreground" />
            {v.status === 'sent' ? (
              <>
                <Icon className="size-3.5 shrink-0" />
                Code sent on {(CHANNEL[v.channel] || CHANNEL.sms).word} to{' '}
                <b className="tabular-nums">{pretty(phone)}</b>
              </>
            ) : (
              <>We&rsquo;ll send a code to <b className="tabular-nums">{pretty(phone)}</b></>
            )}
          </p>
          {/* Always reachable, including after the code is on its way - the
              person who mistyped finds out precisely when nothing arrives. */}
          <button
            type="button" onClick={() => setEditing(true)}
            className="flex items-center gap-1 text-[12px] font-medium underline underline-offset-2"
          >
            <Pencil className="size-3" />
            Change
          </button>
        </div>
      )}

      {v.status === 'sent' && !editing && (
        <div className="space-y-2">
          <Input
            ref={box}
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); v.clearError(); }}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            aria-label="Verification code"
            className="max-w-[9rem] text-center text-lg font-semibold tracking-[0.4em]"
            disabled={busy || v.status === 'verifying'}
          />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <button type="button" className="underline underline-offset-2 disabled:no-underline disabled:opacity-60"
              disabled={v.cooldown > 0} onClick={() => v.send(v.channel)}>
              {v.cooldown > 0 ? `Resend in ${v.cooldown}s` : 'Resend code'}
            </button>
            {other.map((c) => (
              <button key={c} type="button" className="underline underline-offset-2 disabled:no-underline disabled:opacity-60"
                disabled={v.cooldown > 0} onClick={() => v.send(c)}>
                Send by {(CHANNEL[c] || CHANNEL.sms).word} instead
              </button>
            ))}
          </div>
        </div>
      )}

      {v.status === 'verifying' && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="size-3 animate-spin" /> Checking your code&hellip;
        </p>
      )}
      {v.error && <p className="text-xs font-medium text-destructive">{v.error}</p>}
    </div>
  );
}
